<template>
  <header class="top-header" :class="{ 'header-transparent': $route.meta.transparentHeader }">
    <!-- 顶部导航栏 -->
    <div class="top-nav">
      <div v-if="showNavActions" class="nav-left" :class="{ 'force-show': isDesktop && isFlashOptions }">
        <router-link to="/" class="brand-mark">
          <img src="@/assets/images/hamilton-lane-logo-transparent.png" alt="HamiltonLane" class="logo-emblem" />
        </router-link>
      </div>

      <!-- Desktop navigation links (only on flash-options where sidebar is hidden) -->
      <nav v-if="showNavActions && isDesktop && $route.path === '/flash-options'" class="desktop-nav">
        <router-link to="/" class="desktop-nav-link" :class="{ active: $route.path === '/' }">{{ $t('bottomNav.首页') }}</router-link>
        <router-link to="/flash-options" class="desktop-nav-link" :class="{ active: $route.path === '/flash-options' }">{{ $t('bottomNav.闪期权') }}</router-link>
        <router-link to="/computing" class="desktop-nav-link" :class="{ active: $route.path.startsWith('/computing') }">{{ $t('bottomNav.算力申请') }}</router-link>
        <router-link to="/assets" class="desktop-nav-link" :class="{ active: $route.path === '/assets' }">{{ $t('bottomNav.资产') }}</router-link>
      </nav>

      <div class="nav-spacer desktop-only"></div>

      <div v-if="showNavActions" class="nav-right">
        <!-- Wallet -->
        <div class="nav-icon" :class="{ 'wallet-connected': isConnected }" @click="handleWalletClick">
          <img src="@/assets/images/wallet-icon.png" alt="" />
        </div>

        <!-- Language Selection -->
        <div class="nav-icon language-selector">
          <img src="@/assets/images/language-icon.png" alt="" @click="toggleLanguageDropdown" />
          <div v-if="showLanguageDropdown" class="language-dropdown" @click.stop>
            <div class="dropdown-header">{{ $t('assets.选择语言') }}</div>
            <div
              v-for="language in languages"
              :key="language.code"
              class="language-option"
              :class="{ active: language.code === selectedLanguage }"
              @click="selectLanguage(language.code)"
            >
              <img :src="language.flag" :alt="language.name" class="flag-icon" />
              <span>{{ language.name }}</span>
              <div v-if="language.code === selectedLanguage" class="active-indicator"></div>
            </div>
          </div>
        </div>

      </div>

      <!-- Flash Options history icon (mobile only) -->
      <div v-if="$route.meta.hideHeaderActions && !isDesktop" class="nav-right">
        <div class="nav-icon hist-icon" @click="toggleFlashHistory">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
      </div>
    </div>

    <!-- 钱包连接加载遮罩 -->
    <teleport to="body">
      <div v-if="isConnecting" class="wallet-loading-overlay">
        <div class="wallet-loading-modal">
          <div class="loading-spinner-large"></div>
          <h3 class="loading-title">{{ $t('topHeader.连接中') }}</h3>
          <p class="loading-subtitle">{{ $t('topHeader.请在钱包中确认') }}</p>
        </div>
      </div>
    </teleport>

    <!-- 钱包连接成功提示 -->
    <teleport to="body">
      <div v-if="showSuccessModal" class="wallet-success-overlay">
        <div class="wallet-success-modal">
          <div class="success-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#F0B90B" stroke-width="2"/>
              <path d="m9 12 2 2 4-4" stroke="#F0B90B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3 class="success-title">{{ $t('topHeader.连接成功') }}</h3>
          <p class="success-subtitle">{{ $t('topHeader.钱包已成功连接') }}</p>
        </div>
      </div>
    </teleport>

    <!-- 钱包详情弹窗 -->
    <teleport to="body">
      <div v-if="showWalletModal" class="wallet-modal-overlay" @click="closeWalletModal">
        <div class="wallet-modal" @click.stop>
          <div class="wallet-modal-header">
            <h3>{{ $t('topHeader.钱包信息') }}</h3>
            <button class="close-btn" @click="closeWalletModal">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div class="wallet-modal-content">
            <div class="wallet-overview-card">
              <div class="wallet-status">
                <div class="status-indicator connected"></div>
                <span class="status-text">{{ $t('topHeader.已连接') }}</span>
              </div>
              <span class="wallet-network-chip">{{ selectedNetwork.toUpperCase() }}</span>
            </div>

            <div class="wallet-address-section">
              <div class="wallet-address-head">
                <label>{{ $t('topHeader.钱包地址') }}</label>
                <button
                  type="button"
                  class="copy-btn"
                  :class="{ copied: addressCopied }"
                  @click="copyWalletAddress"
                >
                  <svg v-if="!addressCopied" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="9" y="9" width="10" height="10" rx="2" stroke="currentColor" stroke-width="2" />
                    <rect x="5" y="5" width="10" height="10" rx="2" stroke="currentColor" stroke-width="2" opacity="0.6" />
                  </svg>
                  <svg v-else viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M6 12l4 4 8-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
              </div>
              <div class="address-display">
                <span class="full-address">{{ walletInfo?.address || '--' }}</span>
              </div>
            </div>
          </div>

          <div class="wallet-modal-footer">
            <button class="disconnect-btn" @click="disconnectWallet">
              {{ $t('topHeader.断开连接') }}
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </header>
</template>

<script>
import { useI18n } from 'vue-i18n'
import * as walletApi from '@/utils/walletApi'
import {
  isMetaMaskAvailable,
  formatAddress,
  watchAccountChange,
  watchNetworkChange
} from '@/utils/wallet'
import { api, reportUserActivity } from '@/services/api'
import { connectEvmWallet } from '@/utils/evmConnect'

export default {
  name: 'TopHeader',
  setup() {
    const { t } = useI18n()
    return { t }
  },
  data() {
    return {
      showLanguageDropdown: false,
      showWalletModal: false,
      showSuccessModal: false,
      selectedNetwork: 'erc20-usdc',
      selectedLanguage: localStorage.getItem('selected_language') || localStorage.getItem('tenant_default_language') || 'en',
      isConnected: false,
      isConnecting: false,
      addressCopied: false,
      userInfo: null,
      walletInfo: null,
      allLanguages: [
        { code: 'fr', name: 'Français', flag: require('@/assets/flags/fr.svg') },
        { code: 'en', name: 'English', flag: require('@/assets/flags/eng.png') },
        { code: 'zh-cn', name: '中文（简体）', flag: require('@/assets/flags/china.svg') },
        { code: 'zh-tw', name: '中文（繁體）', flag: require('@/assets/flags/zh-tw.svg') },
        { code: 'pt', name: 'Português', flag: require('@/assets/flags/pro.svg') },
        { code: 'es', name: 'español', flag: require('@/assets/flags/esp.svg') },
        { code: 'pl', name: 'Polski', flag: require('@/assets/flags/poi.svg') },
        { code: 'de', name: 'Deutsch', flag: require('@/assets/flags/deu.svg') },
        { code: 'it', name: 'Italiano', flag: require('@/assets/flags/italy.svg') },
        { code: 'tr', name: 'Türkçe', flag: require('@/assets/flags/tr.svg') }
      ],
      enabledLanguageCodes: ['fr','en','zh-cn','zh-tw','pt','es','pl','de','it','tr'],
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 0
    }
  },
  computed: {
    isDesktop() {
      return this.windowWidth >= 769
    },
    isFlashOptions() {
      return this.$route.path === '/flash-options'
    },
    showNavActions() {
      if (!this.$route.meta.hideHeaderActions) return true
      return this.isDesktop
    },
    languages() {
      return this.allLanguages.filter(lang => this.enabledLanguageCodes.includes(lang.code))
    },
    walletButtonText() {
      if (this.isConnecting) {
        return this.$t('topHeader.登录进行中')
      }
      if (this.isConnected && this.walletInfo) {
        return formatAddress(this.walletInfo.address)
      }
      return this.$t('topHeader.连接钱包')
    }

  },
  methods: {
    async copyWalletAddress() {
      const address = this.walletInfo?.address
      if (!address) return

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(address)
        } else {
          const textArea = document.createElement('textarea')
          textArea.value = address
          textArea.setAttribute('readonly', '')
          textArea.style.position = 'absolute'
          textArea.style.left = '-9999px'
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
        }

        this.addressCopied = true
        setTimeout(() => {
          this.addressCopied = false
        }, 1200)
      } catch (error) {
        console.warn('copy wallet address failed:', error)
      }
    },
    _onResize() {
      this.windowWidth = window.innerWidth
    },
    toggleFlashHistory() {
      window.dispatchEvent(new CustomEvent('toggle-flash-history'))
    },
    toggleLanguageDropdown() {
      this.showLanguageDropdown = !this.showLanguageDropdown
    },
    selectLanguage(languageCode) {
      const from = this.selectedLanguage || this.$i18n?.locale || localStorage.getItem('selected_language') || ''
      this.selectedLanguage = languageCode
      this.showLanguageDropdown = false

      // 保存到localStorage
      localStorage.setItem('selected_language', languageCode)
      if (languageCode && languageCode !== from) {
        reportUserActivity('language_change', { from, to: languageCode })
      }

      // 更新i18n语言
      if (this.$i18n) {
        this.$i18n.locale = languageCode
      }

      // 更新页面标题
      if (window.updateCurrentPageTitle) {
        window.updateCurrentPageTitle()
      }

      console.log('Language changed to:', languageCode)
    },

    // 同步语言设置
    syncLanguageSettings() {
      const savedLanguage = localStorage.getItem('selected_language')
      if (savedLanguage && savedLanguage !== this.selectedLanguage) {
        this.selectedLanguage = savedLanguage
        if (this.$i18n) {
          this.$i18n.locale = savedLanguage
        }
        console.log('Language synced to:', savedLanguage)
      }
    },

    // 从API加载启用的语言列表，5秒超时防卡住
    async loadEnabledLanguages() {
      try {
        const data = await api.get('/system/config')
        if (data.success && data.data) {
          if (data.data.language) {
            const enabled = data.data.language.enabled_languages
            if (Array.isArray(enabled) && enabled.length > 0) {
              this.enabledLanguageCodes = enabled
            }
          }
          // 存储租户默认语言到 localStorage，供 i18n 初始化和其他组件使用
          const sysDefaultLang = data.data.system?.defaultLanguage || data.data.system?.default_language
          if (sysDefaultLang) {
            const available = this.$i18n?.availableLocales || []
            const normalized = available.includes(sysDefaultLang)
              ? sysDefaultLang
              : (String(sysDefaultLang).startsWith('zh') ? 'zh-cn' : 'en')
            localStorage.setItem('tenant_default_language', normalized)
            // 如果用户从未选择过语言，应用租户默认语言
            if (!localStorage.getItem('selected_language')) {
              this.selectedLanguage = normalized
              if (this.$i18n) {
                this.$i18n.locale = normalized
              }
            } else if (available.length && !available.includes(this.$i18n.locale)) {
              // 纠正本地缓存里不存在的语言（例如旧的 zh-tw）
              this.selectedLanguage = normalized
              localStorage.setItem('selected_language', normalized)
              this.$i18n.locale = normalized
            }
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('加载语言设置失败:', error)
        }
      }
    },

    // 钱包连接相关方法
    async handleWalletClick() {
      // 防止重复点击
      if (this.isConnecting) {
        return
      }

      if (this.isConnected) {
        // 已连接，显示钱包详情弹窗
        this.showWalletModal = true
      } else {
        // 未连接，进行钱包连接
        await this.connectWallet()
      }
    },

    // 关闭钱包弹窗
    closeWalletModal() {
      this.showWalletModal = false
    },

    // 断开钱包连接
    disconnectWallet() {
      // 清除本地存储
      localStorage.removeItem('wallet_token')
      localStorage.removeItem('user_info')
      localStorage.removeItem('wallet_info')

      // 重置状态
      this.isConnected = false
      this.userInfo = null
      this.walletInfo = null

      // 关闭弹窗
      this.showWalletModal = false

      // 显示断开成功提示
      this.$toast?.success?.(this.$t('topHeader.已断开连接')) ||
      alert(this.$t('topHeader.已断开连接'))

      // 刷新页面或重定向到首页
      window.location.reload()
    },


    async connectWallet() {
      // 防止重复连接
      if (this.isConnecting) {
        console.log('钱包连接正在进行中，跳过重复请求')
        return
      }

      this.isConnecting = true
      console.log('开始钱包连接流程...')

      try {
        // 使用 connectWalletProvider 方法进行钱包检测和连接
        const walletData = await this.connectWalletProvider()
        if (!walletData) {
          return
        }

        console.log('钱包连接成功，获取到地址:', walletData.address)
        console.log('调用后端API进行用户登录/注册...')

        // 调用后端 API 进行用户登录/注册（传递当前域名）
        console.log('当前域名:', window.location.hostname)
        const response = await walletApi.connect(walletData.address, walletData.addressType)

        console.log('后端API响应:', response)

        if (response.success) {
          console.log('用户登录/注册成功')
          this.isConnected = true
          this.userInfo = response.data.user
          this.walletInfo = response.data.wallet

          // 保存用户数据到本地存储
          console.log('保存用户数据到本地存储...')
          this.saveUserData(response.data)

          // 显示连接成功弹窗
          console.log('显示连接成功弹窗')
          this.showSuccessModal = true

          // 2秒后自动关闭成功提示
          setTimeout(() => {
            this.showSuccessModal = false
          }, 2000)

          // 触发全局事件，通知其他组件用户已登录
          console.log('触发用户连接事件...')
          this.$emit('user-connected', response.data)

          // 同时发出全局事件，让 Investment.vue 能接收到
          window.dispatchEvent(new CustomEvent('user-connected', {
            detail: response.data
          }))
        } else {
          console.error('后端API返回失败:', response.message)
          alert(this.$t('topHeader.钱包连接失败') + ': ' + response.message)
        }
      } catch (error) {
        console.error('钱包连接失败:', error)
        console.error('错误详情:', {
          code: error.code,
          message: error.message,
          status: error.status,
          stack: error.stack
        })

        // 更友好的错误处理
        if (error.code === 'AUTH_EXPIRED' || error.status === 401) {
          console.log('认证失败，重置连接状态')
          alert(this.$t('topHeader.认证失败，请重试连接钱包'))
          this.resetConnectionState()
        } else if (error.message && error.message.includes('already pending')) {
          console.log('钱包连接请求正在处理中')
          alert(this.$t('topHeader.钱包连接请求正在处理中，请稍等或刷新页面后重试'))
        } else if (error.code === 4001) {
          console.log('用户取消了连接请求')
          // 用户取消连接，无需弹窗提示
        } else if (error.code === -32002) {
          console.log('钱包连接请求正在等待处理')
          alert(this.$t('topHeader.钱包连接请求正在等待处理，请在钱包中确认'))
        } else {
          console.log('钱包连接失败，显示通用错误消息')
          alert(this.$t('topHeader.钱包连接失败') + ': ' + (error.message || this.$t('topHeader.未知错误')))
        }
      } finally {
        console.log('钱包连接流程结束，重置连接状态')
        this.isConnecting = false
      }
    },

    // 连接钱包提供商（从原来的 connectWallet 方法提取）
    async connectWalletProvider() {
      let walletData = null

      if (window.ethereum) {
        // Coinbase 会把 isMetaMask 设为 true；统一走静默读地址，避免 Base 开通费弹窗
        walletData = await this.connectEthereumWallet()
      } else if (window.okxwallet) {
        walletData = await this.connectOKXWallet()
      } else if (window.BinanceChain) {
        walletData = await this.connectBinanceWallet()
      } else if (window.solana && window.solana.isPhantom) {
        walletData = await this.connectPhantom()
      } else if (window.tronWeb) {
        walletData = await this.connectTronLink()
      } else {
        return null
      }

      return walletData
    },


    // 显示钱包调试信息（用于手机测试）
    showWalletDebugInfo(isOnChain, isMetaMask, isBitget) {
      const debugInfo = `钱包检测结果:\n` +
        `OnChain: ${isOnChain}\n` +
        `MetaMask: ${isMetaMask}\n` +
        `Bitget: ${isBitget}\n` +
        `地址: ${window.ethereum?.selectedAddress || '未连接'}`

      // 在页面上显示调试信息（使用alert代替showToast）
      alert(debugInfo)

      // 也保存到sessionStorage供查看
      sessionStorage.setItem('wallet_debug_info', debugInfo)
    },


    async connectMetaMask() {
      return this.connectEthereumWallet()
    },

    async connectTrustWallet() {
      return this.connectEthereumWallet()
    },

    async connectCoinbaseWallet() {
      return this.connectEthereumWallet()
    },

    // 通用以太坊钱包连接（Coinbase 静默读地址，其它钱包才 requestAccounts）
    async connectEthereumWallet() {
      const data = await connectEvmWallet()
      if (!data?.address) {
        throw new Error(this.$t('topHeader.未找到钱包账户'))
      }
      return {
        address: data.address,
        addressType: data.addressType
      }
    },

    // OKX Wallet连接
    async connectOKXWallet() {
      const accounts = await window.okxwallet.request({ method: 'eth_requestAccounts' })
      const chainId = await window.okxwallet.request({ method: 'eth_chainId' })
      return {
        address: accounts[0],
        addressType: this.getAddressTypeByChainId(chainId)
      }
    },

    // Binance Wallet连接
    async connectBinanceWallet() {
      const accounts = await window.BinanceChain.request({ method: 'eth_requestAccounts' })
      const chainId = await window.BinanceChain.request({ method: 'eth_chainId' })
      return {
        address: accounts[0],
        addressType: this.getAddressTypeByChainId(chainId)
      }
    },

    // Phantom连接 (Solana)
    async connectPhantom() {
      const response = await window.solana.connect()
      return {
        address: response.publicKey.toString(),
        addressType: 3 // Solana
      }
    },

    // TronLink连接
    async connectTronLink() {
      // 等待TronWeb初始化
      await new Promise(resolve => {
        const check = () => {
          if (window.tronWeb && window.tronWeb.defaultAddress.base58) {
            resolve()
          } else {
            setTimeout(check, 100)
          }
        }
        check()
      })

      return {
        address: window.tronWeb.defaultAddress.base58,
        addressType: 1 // Tron
      }
    },

    // 根据链ID获取地址类型
    getAddressTypeByChainId(chainId) {
      const chainIdNum = parseInt(chainId, 16)
      switch (chainIdNum) {
        case 1: case 5: case 11155111: return 0 // Ethereum
        case 56: case 97: return 2 // BSC
        case 137: case 80001: return 2 // Polygon
        default: return 0
      }
    },

    showWalletMenu() {
      // 简单的确认对话框，实际项目中可以用更好的 UI
      if (confirm(this.$t('topHeader.是否断开钱包连接？'))) {
        this.disconnectWallet()
      }
    },

    // 重置连接状态（用于处理异常情况）
    resetConnectionState() {
      console.log('重置连接状态...')
      this.isConnecting = false
      this.isConnected = false
      this.userInfo = null
      this.walletInfo = null

      // 清除本地存储
      localStorage.removeItem('wallet_token')
      localStorage.removeItem('user_info')
      localStorage.removeItem('wallet_info')
      localStorage.removeItem('wallet_address')
      localStorage.removeItem('address')
      localStorage.removeItem('addressType')

      console.log('连接状态已重置')
    },




    // 保存用户数据
    saveUserData(data) {
      this.isConnected = true
      this.userInfo = data.user
      this.walletInfo = data.wallet

      // 保存到本地存储
      localStorage.setItem('wallet_token', data.token)
      localStorage.setItem('user_info', JSON.stringify(data.user))
      localStorage.setItem('wallet_info', JSON.stringify(data.wallet))

      // ✅ 单独保存钱包地址，以便 api.js 能正确获取
      if (data.wallet && data.wallet.address) {
        localStorage.setItem('wallet_address', data.wallet.address)
        console.log('✅ 保存钱包地址到localStorage:', data.wallet.address)
      }

      console.log('保存钱包信息到localStorage:', data.wallet)

      // 触发全局事件
      this.$emit('user-connected', data)
    },


    // 后台一键登录：消费 ?admin_token=
    async consumeAdminLoginToken() {
      try {
        const url = new URL(window.location.href)
        const token = url.searchParams.get('admin_token')
        if (!token) return false

        localStorage.setItem('wallet_token', token)
        const response = await walletApi.getWalletDetails()
        if (!response?.success || !response.data?.user || !response.data?.wallet) {
          localStorage.removeItem('wallet_token')
          console.warn('admin_token 校验失败', response)
          return false
        }

        this.saveUserData({
          token,
          user: response.data.user,
          wallet: response.data.wallet
        })

        url.searchParams.delete('admin_token')
        const cleaned = `${url.pathname}${url.search}${url.hash}`
        window.history.replaceState({}, document.title, cleaned || '/')
        console.log('✅ 后台一键登录成功')
        this.$emit('user-connected', {
          token,
          user: response.data.user,
          wallet: response.data.wallet
        })
        return true
      } catch (e) {
        console.warn('消费 admin_token 失败:', e)
        return false
      }
    },

    // 检查用户登录状态
    async checkLoginStatus() {
      console.log('检查钱包连接状态...')

      // 直接从localStorage检查连接状态，不调用API
      const walletInfo = localStorage.getItem('wallet_info')
      const userInfo = localStorage.getItem('user_info')
      const walletToken = localStorage.getItem('wallet_token')

      if (walletInfo && userInfo && walletToken) {
        try {
          const response = await walletApi.getUserInfo()
          if (response.success) {
            this.isConnected = true
            this.userInfo = JSON.parse(userInfo)
            this.walletInfo = JSON.parse(walletInfo)
            console.log('✅ 钱包连接状态已从localStorage恢复:', {
              userInfo: this.userInfo,
              walletInfo: this.walletInfo
            })
            return
          }
        } catch (error) {
          console.warn('登录状态校验失败，重置本地连接状态:', error)
        }
        this.resetConnectionState()
      } else if (walletInfo) {
        // 只有钱包信息，没有用户信息和token
        this.walletInfo = JSON.parse(walletInfo)
        console.log('⚠️ 检测到钱包地址但缺少用户信息，需要重新连接')
        this.resetConnectionState()
      } else {
        console.log('❌ 未检测到钱包连接')
        this.resetConnectionState()
      }
    },

    // 设置钱包监听器
    setupWalletListeners() {
      if (!isMetaMaskAvailable()) return

      // 监听账户变化
      watchAccountChange((newAccount) => {
        if (!newAccount && this.isConnected) {
          // 用户在 MetaMask 中断开了连接
          this.disconnectWallet()
        }
      })

      // 监听网络变化
      watchNetworkChange((chainId) => {
        console.log('网络已切换:', chainId)
        // 可以在这里处理网络切换逻辑
      })
    },

    // 处理全局钱包连接请求（替代原授权流程，现只触发钱包连接）
    async handleWalletConnectionRequest() {
      console.log('收到全局钱包连接请求')
      if (!this.isConnected) {
        await this.connectWallet()
      }
    },

  },
  async mounted() {
    // 后台一键登录：优先消费 URL 中的 admin_token
    const fromAdmin = await this.consumeAdminLoginToken()
    if (!fromAdmin) {
      this.checkLoginStatus()
    }

    // 设置钱包监听器
    this.setupWalletListeners()

    // 同步语言设置
    this.syncLanguageSettings()

    // 加载启用的语言列表
    this.loadEnabledLanguages()

    // 监听全局钱包连接请求（其他组件通过此事件触发钱包连接）
    window.addEventListener('start-wallet-authorization', this.handleWalletConnectionRequest)

    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target)) {
        this.showLanguageDropdown = false
      }
    })

    window.addEventListener('resize', this._onResize)
  },

  beforeUnmount() {
    window.removeEventListener('start-wallet-authorization', this.handleWalletConnectionRequest)
    window.removeEventListener('resize', this._onResize)
  }
}
</script>

<style scoped>
.top-header {
  background:
    radial-gradient(circle at 8% 0%, rgba(47, 241, 210, 0.14), transparent 42%),
    radial-gradient(circle at 92% 0%, rgba(255, 111, 181, 0.08), transparent 38%),
    linear-gradient(180deg, rgba(9, 24, 44, 0.96), rgba(8, 20, 36, 0.98));
  padding: 0;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  border-bottom: 1px solid rgba(88, 126, 167, 0.35);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(12px);
}
.top-header.header-transparent {
  background: transparent;
  border-bottom: none;
  box-shadow: none;
  backdrop-filter: none;
}

/* 顶部导航栏 */
.top-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 13px 16px;
  min-height: 60px;
  background: transparent;
}

.nav-left .brand-mark {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.nav-left .logo-emblem {
  width: auto;
  height: 36px;
  max-width: min(200px, 46vw);
  display: block;
  object-fit: contain;
}

.nav-left .brand-copy {
  display: grid;
  line-height: 1;
}

.brand-line-1 {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
}

.brand-title {
  color: #eaf2fb;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.brand-miner {
  color: #2ff1d2;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.brand-line-2 {
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.brand-powered {
  color: rgba(132, 191, 215, 0.88);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.brand-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #2ff1d2;
  box-shadow: 0 0 0 3px rgba(47, 241, 210, 0.18);
}

.brand-status-text {
  color: #d7e4f1;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
}

.nav-right {
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 4px;
  border-radius: 14px;
  border: 1px solid rgba(92, 126, 168, 0.38);
  background: linear-gradient(180deg, rgba(20, 36, 60, 0.92), rgba(12, 25, 45, 0.94));
}



.nav-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid rgba(94, 129, 170, 0.34);
  background: rgba(13, 26, 45, 0.82);
  box-shadow: none;
  padding: 6px;
}

.nav-icon:hover {
  border-color: rgba(47, 241, 210, 0.5);
  background: rgba(47, 241, 210, 0.12);
  box-shadow: 0 0 10px rgba(47, 241, 210, 0.2);
  transform: translateY(-1px);
}

.nav-icon.wallet-connected {
  border-color: rgba(47, 241, 210, 0.6);
  background: rgba(47, 241, 210, 0.2);
  box-shadow: 0 0 10px rgba(47, 241, 210, 0.3);
}

.nav-icon.wallet-connected > img {
  filter: brightness(0) saturate(100%) invert(80%) sepia(63%) saturate(564%) hue-rotate(103deg) brightness(101%) contrast(94%);
}

.nav-icon.wallet-connected:hover {
  border-color: rgba(47, 241, 210, 0.72);
  background: rgba(47, 241, 210, 0.24);
  box-shadow: 0 0 14px rgba(47, 241, 210, 0.36);
}

.nav-icon > img {
  width: 17px;
  height: 17px;
  object-fit: contain;
  filter: brightness(0) saturate(100%) invert(91%) sepia(10%) saturate(308%) hue-rotate(177deg) brightness(102%) contrast(95%);
  opacity: 1;
}

.nav-icon:hover > img {
  filter: brightness(0) saturate(100%) invert(80%) sepia(63%) saturate(564%) hue-rotate(103deg) brightness(101%) contrast(94%);
}

.hist-icon { color: rgba(200,220,240,0.8); }
.hist-icon:hover { color: #2ff1d2; }
.hist-icon svg { display: block; }

/* Desktop navigation - hidden on mobile */
.desktop-nav {
  display: none;
}

.brand-mark {
  text-decoration: none;
}

@media (min-width: 769px) {
  .top-header {
    border-bottom: 1px solid rgba(255,255,255,0.04);
    box-shadow: none;
    background: var(--po-bg-dark, #131a24) !important;
  }

  .top-nav {
    padding: 0 24px;
    min-height: var(--po-header-h, 48px);
    gap: 12px;
  }

  .nav-left {
    display: none;
  }
  .nav-left.force-show {
    display: flex !important;
  }

  .nav-spacer.desktop-only { flex: 1; }

  .desktop-nav {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .desktop-nav-link {
    color: var(--po-text-secondary, #8493a8);
    text-decoration: none;
    font-size: 13px;
    font-weight: 600;
    padding: 6px 14px;
    border-radius: 6px;
    transition: all 0.15s ease;
    white-space: nowrap;
    border: none;
    background: none;
  }

  .desktop-nav-link:hover {
    color: var(--po-text, #e8edf3);
    background: rgba(255,255,255,0.04);
  }

  .desktop-nav-link.active {
    color: var(--po-green, #2ecc71);
    background: rgba(46,204,113,0.08);
  }

  .nav-right {
    border: none;
    background: none;
    padding: 0;
    gap: 8px;
  }

  .nav-icon {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    transition: background 0.15s ease;
  }

  .nav-icon:hover {
    background: rgba(255,255,255,0.06);
  }

  .nav-icon img {
    width: 18px;
    height: 18px;
    opacity: 0.8;
  }

  .nav-icon:hover img {
    opacity: 1;
  }
}


/* 语言和网络选择器需要相对定位 */
.language-selector,
.network-selector {
  position: relative;
}

/* 下拉菜单（与首页 #10213b / 青绿主题一致） */
.language-dropdown,
.network-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 10px;
  background: linear-gradient(180deg, #10213b 0%, #0c1a2e 100%);
  border-radius: 14px;
  border: 1px solid #233a5b;
  min-width: 240px;
  padding: 0 0 10px;
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.48),
    0 0 0 1px rgba(47, 241, 210, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  z-index: 1001;
  overflow: hidden;
  backdrop-filter: blur(12px);
}

.dropdown-header {
  padding: 14px 16px;
  color: #2fd9c1;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-bottom: 1px solid rgba(35, 58, 91, 0.95);
  background: rgba(15, 29, 52, 0.4);
}

.language-option,
.network-option {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 6px 8px 0;
  padding: 11px 14px;
  border-radius: 10px;
  border: 1px solid rgba(88, 126, 167, 0.22);
  background: linear-gradient(180deg, rgba(16, 31, 55, 0.52), rgba(11, 22, 42, 0.72));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  color: #aec0d2;
  cursor: pointer;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  position: relative;
}

.language-dropdown .dropdown-header + .language-option,
.network-dropdown .dropdown-header + .network-option {
  margin-top: 10px;
}

.language-option:last-child,
.network-option:last-child {
  margin-bottom: 2px;
}

.language-option:hover:not(.active),
.network-option:hover:not(.active) {
  background: linear-gradient(180deg, rgba(20, 40, 65, 0.62), rgba(14, 28, 50, 0.82));
  border-color: rgba(88, 126, 167, 0.38);
  color: #dce6f0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.language-option.active,
.network-option.active {
  background: linear-gradient(135deg, rgba(47, 241, 210, 0.16), rgba(47, 241, 210, 0.05));
  border-color: rgba(47, 241, 210, 0.42);
  color: #f0f7fc;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    inset 3px 0 0 #2ff1d2;
}

.active-indicator {
  position: absolute;
  right: 12px;
  width: 8px;
  height: 8px;
  background: #2ff1d2;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(47, 241, 210, 0.55);
}

/* 语言选项中的国旗图标 */
.flag-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  filter: none !important;
}

/* 网络选项中的图标 */
.network-option-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  filter: none !important;
}

/* 钱包详情弹窗样式 */
.wallet-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(5, 8, 13, 0.76);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 14px;
  backdrop-filter: blur(4px);
}

.wallet-modal {
  background: linear-gradient(180deg, rgba(36, 44, 63, 0.98), rgba(28, 35, 53, 0.98));
  border-radius: 20px;
  border: 1px solid rgba(240, 185, 11, 0.22);
  width: 100%;
  max-width: 460px;
  max-height: min(84vh, 720px);
  overflow-y: auto;
  box-shadow: 0 28px 60px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.wallet-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.wallet-modal-header h3 {
  color: #f8fafc;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.02em;
}

.close-btn {
  width: 38px;
  height: 38px;
  background: rgba(148, 163, 184, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.28);
  color: #b8c2d6;
  cursor: pointer;
  padding: 0;
  border-radius: 12px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #e8edf7;
  background: rgba(240, 185, 11, 0.12);
  border-color: rgba(240, 185, 11, 0.38);
}

.wallet-modal-content {
  padding: 18px 24px 24px;
}

/* 连接状态 */
.wallet-overview-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(240, 185, 11, 0.2);
  background: linear-gradient(180deg, rgba(240, 185, 11, 0.13), rgba(240, 185, 11, 0.04));
  margin-bottom: 18px;
}

.wallet-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.status-indicator.connected {
  background: #22c55e;
  box-shadow: 0 0 10px rgba(34, 197, 94, 0.5);
}

.status-text {
  color: #35d173;
  font-weight: 700;
  font-size: 18px;
}

.wallet-network-chip {
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(240, 185, 11, 0.38);
  background: rgba(12, 15, 21, 0.35);
  color: #f4cf63;
  padding: 0 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* 钱包地址部分 */
.wallet-address-section {
  margin-bottom: 0;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(11, 17, 29, 0.22);
  padding: 14px;
}

.wallet-address-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.wallet-address-section label {
  display: block;
  color: #aeb8ca;
  font-size: 14px;
  margin-bottom: 0;
  font-weight: 600;
}

.copy-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: rgba(148, 163, 184, 0.08);
  color: #c6cedd;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.copy-btn svg {
  width: 15px;
  height: 15px;
}

.copy-btn:hover {
  background: rgba(240, 185, 11, 0.14);
  border-color: rgba(240, 185, 11, 0.4);
  color: #f2cc5a;
}

.copy-btn.copied {
  background: rgba(34, 197, 94, 0.12);
  border-color: rgba(34, 197, 94, 0.35);
  color: #22c55e;
}

.address-display {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 12px;
  padding: 12px;
}

.full-address {
  color: #eef2fa;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 16px;
  font-weight: 600;
  word-break: break-all;
  line-height: 1.45;
}

/* 弹窗底部 */
.wallet-modal-footer {
  padding: 16px 24px 22px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.disconnect-btn {
  width: 100%;
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.26), rgba(239, 68, 68, 0.18));
  border: 1px solid rgba(248, 113, 113, 0.44);
  color: #ff6464;
  height: 54px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.disconnect-btn:hover {
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.34), rgba(239, 68, 68, 0.24));
  border-color: rgba(252, 165, 165, 0.6);
}

@media (max-width: 480px) {
  .nav-left .logo-emblem {
    height: 28px;
    max-width: 150px;
  }

  .brand-title,
  .brand-miner {
    font-size: 15px;
  }

  .brand-powered,
  .brand-status-text {
    font-size: 9px;
  }

  .wallet-modal-overlay {
    padding: 12px;
  }

  .wallet-modal {
    border-radius: 18px;
    max-height: 86vh;
  }

  .wallet-modal-header {
    padding: 18px 16px 14px;
  }

  .wallet-modal-header h3 {
    font-size: 22px;
  }

  .wallet-modal-content {
    padding: 14px 16px 18px;
  }

  .wallet-network-chip {
    height: 26px;
    font-size: 10px;
    padding: 0 8px;
  }

  .status-text {
    font-size: 16px;
  }

  .wallet-address-section label {
    font-size: 13px;
  }

  .full-address {
    font-size: 14px;
  }

  .wallet-modal-footer {
    padding: 12px 16px 16px;
  }

  .disconnect-btn {
    height: 48px;
    font-size: 16px;
  }
}

@media (max-width: 375px) {
  .wallet-modal-overlay {
    padding: 10px;
  }

  .wallet-modal-header h3 {
    font-size: 20px;
  }

  .status-text {
    font-size: 15px;
  }

  .full-address {
    font-size: 13px;
  }

  .disconnect-btn {
    height: 44px;
    border-radius: 12px;
    font-size: 15px;
  }
}

/* 钱包连接加载遮罩 */
.wallet-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.wallet-loading-modal {
  background: #2D3748;
  border-radius: 16px;
  padding: 40px;
  text-align: center;
  max-width: 320px;
  width: 90%;
}

.loading-spinner-large {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top: 4px solid #3B82F6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.loading-title {
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.loading-subtitle {
  color: #A0AEC0;
  font-size: 14px;
  margin: 0;
}

/* 钱包连接成功遮罩 */
.wallet-success-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  animation: fadeIn 0.3s ease-out;
}

.wallet-success-modal {
  background: linear-gradient(180deg, rgba(33, 38, 47, 0.98), rgba(23, 27, 34, 0.98));
  border: 1px solid rgba(240, 185, 11, 0.26);
  border-radius: 16px;
  padding: 40px;
  text-align: center;
  max-width: 320px;
  width: 90%;
  animation: slideUp 0.3s ease-out;
}

.success-icon {
  margin: 0 auto 20px;
  animation: scaleIn 0.5s ease-out 0.2s both;
}

.success-title {
  color: #f0b90b;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.success-subtitle {
  color: #A0AEC0;
  font-size: 14px;
  margin: 0;
}

/* 动画效果 */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

</style>
